import DatePicker from "react-datepicker";
import { useField, useFormikContext } from "formik";
import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";

type DateSelectorProps = {
  nullable: boolean,
  type: "date" | "range",
  label: string,
  definition?: string,
  required?: boolean,
  className?: string,
  error?: boolean,
  labelHelper?: string,
  successText?: string,
  name?: string,
  showTimeSelect?: boolean,
  dateFormat?: string,
  value?: string | any,

  // alttaki proplari limitli DateRange icin kullanin
  isLimited?: boolean,
  defaultStartDate?: any,
  defaultEndDate?: any,
  maxOneMonth?: boolean,
  labelInfoIcon?: React.ReactElement,
  resetRangeCounter?: number // formdan range'i sifirladiginda parametreyi 1 arttir. (baslangic degeri 0.)
}
export interface DateSelectorRefProps {
  resetForm: () => void,
  setDateValue: (date: string | any) => void,
}

const DateSelector = React.forwardRef<DateSelectorRefProps, DateSelectorProps>((props: DateSelectorProps | any, ref) => {
  const newDate = props.nullable ? null : new Date();

  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();
  const [dateRange, setDateRange] = useState<any>({
    startDate: props.isLimited ? props.defaultStartDate : newDate,
    endDate: props.isLimited ? props.defaultEndDate : newDate,
  });
  const [dateValue, setDateValue] = useState(newDate);
  const { isResetForm } = useSelector((state: any) => state.formHelper);

  useImperativeHandle(ref, () => ({
    resetForm() {
      resetFormValue();
    },
    setDateValue(date) {
      setDateValue(date);
    },
  }));

  useEffect(() => {
    if (isResetForm) {
      resetFormValue();
    }
  }, [isResetForm]);

  useEffect(() => {
    if (props.type === "range") {
      setFieldValue(field.name, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }
  }, [dateRange]);

  useEffect(() => {
    if (props.type !== "range") {
      setFieldValue(field.name, dateValue);
    }
  }, [dateValue]);

  useEffect(() => {
    if (props.resetRangeCounter > 0) {
      setDateRange({
        startDate: props.defaultStartDate,
        endDate: props.defaultEndDate,
      })
    }
  }, [props.resetRangeCounter])

  const resetFormValue = () => {
    if (props.type == "range") {
      setDateRange({
        startDate: props.isLimited ? props.defaultStartDate : newDate,
        endDate: props.isLimited ? props.defaultEndDate : newDate,
      });
    } else {
      setDateValue(newDate);
    }
  }

  const setStartDate = (val: any) => {
    if (props.isLimited) {
      const maxEndDate = new Date(val);
      maxEndDate.setDate(maxEndDate.getDate() + 30);

      setDateRange({
        startDate: val,
        endDate: null,
      });
    } else {
      setDateRange({
        ...dateRange,
        startDate: val,
      });
    }
  };

  const setEndDate = (val: any) => {
    if (props.isLimited) {
      // Eğer startDate seçilmemişse önce startDate olarak bir değer ata
      if (!dateRange.startDate) {
        const defaultStartDate = props.defaultStartDate || new Date(); // Varsayılan bir başlangıç tarihi ayarla
        setDateRange((prevRange: any) => ({
          ...prevRange,
          startDate: defaultStartDate,
        }));
      }

      const maxEndDate = new Date(dateRange.startDate || new Date());
      maxEndDate.setDate(maxEndDate.getDate() + 30);

      setDateRange((prevRange: any) => ({
        ...prevRange,
        endDate: val > maxEndDate ? maxEndDate : val,
      }));
    } else {
      setDateRange((prevRange: any) => ({
        ...prevRange,
        endDate: val,
      }));
    }
  };

  return (
    <div>
      {props.label && (
        <div className="flex items-center text-sm  mb-1">
          <label className="block text-contrast-70 whitespace-nowrap cursor-default">
            {props.label}
          </label>
          {props.definition && (
            <p className="block text-contrast-70 text-darkblue-400 whitespace-nowrap ml-1">
              ({props.definition})
            </p>
          )}
          {props.required && <p className="block text-contrast-70">*</p>}
          {props.labelInfoIcon ? props.labelInfoIcon : null}
        </div>
      )}

      {props.type === "range" ? (
        <div className="flex flex-row justify-between space-x-2">
          <div className="w-full">
            {props.isLimited ?
              <DatePicker
                autoComplete="off"
                {...field}
                wrapperClassName="w-full"
                className={`p-2 border-gray-300 mr-2  rounded-md-custom focus:outline-none leading-3 h-8 bg-contrast-10 border-b text-sm  w-full ${props.className
                  }  ${props.error ? "border-error" : ""}`}
                selected={dateRange.startDate ? dateRange.startDate : props.defaultStartDate}
                dateFormat={props.dateFormat || "dd.MM.yyyy"}
                onChange={(date) => setStartDate(date)}
                startDate={dateRange.startDate ? dateRange.startDate : props.defaultStartDate}
                endDate={dateRange.endDate ? dateRange.endDate : props.defaultEndSelected}
                maxDate={props.maxOneMonth ? dateRange.endDate ? new Date(dateRange.startDate).setDate(dateRange.startDate.getDate() + 30) : dateRange.endDate : dateRange.endDate} // Maksimum bitiş tarihi 30 gün sonrası
                showTimeSelect={props.showTimeSelect || false}
                placeholderText=""
              /> :
              <DatePicker
                autoComplete="off"
                {...field}
                wrapperClassName="w-full"
                className={`p-2 border-gray-300 mr-2  rounded-md-custom focus:outline-none leading-3 h-8 bg-contrast-10 border-b text-sm  w-full ${props.className
                  }  ${props.error ? "border-error" : ""}`}
                selected={dateRange.startDate}
                dateFormat={props.dateFormat || "dd.MM.yyyy"}
                onChange={(date) => setStartDate(date)}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                maxDate={dateRange.endDate}
                showTimeSelect={props.showTimeSelect || false}
                popperModifiers={[
                  {
                    name: "flip",
                    enabled: false,
                  },
                ]}
              />
            }
          </div>
          <div className="w-full">
            {props.isLimited ?
              <DatePicker
                autoComplete="off"
                wrapperClassName="w-full"
                className={`p-2 border-gray-300 mr-2 rounded-md-custom focus:outline-none leading-3 h-8 bg-contrast-10 border-b text-sm  w-full ${props.className
                  }  ${props.error ? "border-error" : ""}`}
                dateFormat={props.dateFormat || "dd.MM.yyyy"}
                selected={dateRange.endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={dateRange.startDate ? dateRange.startDate : props.defaultStartDate}
                endDate={dateRange.endDate ? dateRange.endDate : null}
                minDate={dateRange.startDate ? dateRange.startDate : props.defaultStartDate} // Bitiş tarihi, başlangıç tarihinden önce olamaz
                maxDate={dateRange.startDate ? new Date(dateRange.startDate).setDate(dateRange.startDate.getDate() + 30) : dateRange.startDate} // Maksimum bitiş tarihi 30 gün sonrası
                showTimeSelect={props.showTimeSelect || false}
              /> :
              <DatePicker
                autoComplete="off"
                wrapperClassName="w-full"
                className={`p-2 border-gray-300 mr-2 rounded-md-custom focus:outline-none leading-3 h-8 bg-contrast-10 border-b text-sm  w-full ${props.className
                  }  ${props.error ? "border-error" : ""}`}
                dateFormat={props.dateFormat || "dd.MM.yyyy"}
                selected={dateRange.endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                maxDate={newDate}
                minDate={dateRange.startDate}
                showTimeSelect={props.showTimeSelect || false}
                popperModifiers={[
                  {
                    name: "flip",
                    enabled: false,
                  },
                ]}
              />
            }
          </div>
        </div>
      ) : (
        <DatePicker
          autoComplete="off"
          {...props}
          {...field}
          selected={dateValue}
          dateFormat={props.dateFormat || "dd.MM.yyyy"}
          minDate={newDate}
          className={`border-b text-sm  leading-3 rounded-md-custom	p-2 bg-contrast-10 h-8  border-gray-300 mr-2 focus:outline-none w-full ${props.className
            }  ${props.error ? "border-error" : ""}`}
          onChange={(date) => {
            setDateValue(date)
          }}
          showTimeSelect={props.showTimeSelect || false}
          timeCaption="Saat"
          popperModifiers={[
            {
              name: "flip",
              enabled: false,
            },
          ]}
        />
      )}

      {props.labelHelper && (
        <p className="mt-2 text-2xs text-contrast-70">{props.labelHelper}</p>
      )}
      {(meta.error && !dateValue) && (
        <p className="mt-2 text-sm text-error-text">{meta.error}</p>
      )}
      {props.successText && (
        <p className="mt-2 text-sm text-success-text">{props.successText}</p>
      )}
    </div>
  );
})

export default DateSelector